import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Row, Col, Card, Button, Modal, Form, Badge } from 'react-bootstrap';
import axiosInstance from '../../services/AxiosInstance';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import { listStaffByCorporate, activateStaff, deactivateStaff } from '../../services/StaffService';
import '../tasks/tasklist.css';

const StaffList = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [subDepartmentFilter, setSubDepartmentFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000, closeOnClick: true });

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${user.corporateId}`);
      setBranches(res.data.data || res.data || []);
    } catch (err) {
      console.error('Branch fetch error:', err);
    }
  };

  // Fetch departments
  const fetchDepartments = async (branchId = null) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const url = branchId
        ? `/settings/departments?corporate_id=${user.corporateId}&branch_id=${branchId}`
        : `/settings/departments?corporate_id=${user.corporateId}`;
      const res = await axiosInstance.get(url);
      const data = res.data.data || res.data || [];
      setDepartments(data);
    } catch (err) {
      console.error('Department fetch error:', err);
    }
  };

  // Fetch sub-departments
  const fetchSubDepartments = async (departmentId = null) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const url = departmentId
        ? `/settings/subDepartments?corporate_id=${user.corporateId}&department_id=${departmentId}`
        : `/settings/subDepartments?corporate_id=${user.corporateId}`;
      const res = await axiosInstance.get(url);
      setSubDepartments(res.data.data || res.data || []);
    } catch (err) {
      console.error('SubDepartment fetch error:', err);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchDepartments();
    fetchSubDepartments();
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [currentPage, search, statusFilter, branchFilter, departmentFilter, subDepartmentFilter]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      const corporateId = user?.corporateId || user?.id;
      
      const params = {
        page: currentPage,
        limit: 10,
      };
      
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (branchFilter) params.branch_id = branchFilter;
      if (departmentFilter) params.department_id = departmentFilter;
      if (subDepartmentFilter) params.sub_department_id = subDepartmentFilter;

      const res = await listStaffByCorporate(corporateId, params);
      
      if (res.data.success) {
        setStaff(res.data.data || []);
        setPagination(res.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      } else {
        setStaff([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      }
    } catch (error) {
      console.error('Staff list fetch error:', error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || 'Failed to fetch staff');
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setDeleteLoading(true);
    try {
      await deactivateStaff(selected.id);
      notifySuccess('Staff deactivated successfully');
      setShowDeleteModal(false);
      setSelected(null);
      fetchStaff();
    } catch (error) {
      console.error('Delete staff error:', error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || 'Failed to deactivate staff');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleActivate = async (staffId) => {
    try {
      await activateStaff(staffId);
      notifySuccess('Staff activated successfully');
      fetchStaff();
    } catch (error) {
      console.error('Activate staff error:', error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || 'Failed to activate staff');
    }
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleBranchChange = (e) => {
    setBranchFilter(e.target.value);
    setDepartmentFilter('');
    setSubDepartmentFilter('');
    setCurrentPage(1);
    if (e.target.value) {
      fetchDepartments(e.target.value);
      fetchSubDepartments(null);
    } else {
      fetchDepartments();
      fetchSubDepartments();
    }
  };

  const handleDepartmentChange = (e) => {
    setDepartmentFilter(e.target.value);
    setSubDepartmentFilter('');
    setCurrentPage(1);
    if (e.target.value) {
      fetchSubDepartments(e.target.value);
    } else {
      fetchSubDepartments();
    }
  };

  const handleSubDepartmentChange = (e) => {
    setSubDepartmentFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Define columns for CommonTable
  const columns = [
    {
      key: 'index',
      header: '#',
      accessor: (row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1,
    },
    {
      key: 'employee_code',
      header: 'Employee Code',
      accessor: (row) => row.employee_code || 'N/A',
    },
    {
      key: 'name',
      header: 'Name',
      accessor: (row) => {
        const parts = [];
        if (row.first_name) parts.push(row.first_name);
        if (row.middle_name) parts.push(row.middle_name);
        if (row.last_name) parts.push(row.last_name);
        return parts.length > 0 ? parts.join(' ') : 'N/A';
      },
    },
    {
      key: 'company',
      header: 'Company',
      accessor: (row) => row.Corporate?.name || 'N/A',
    },
    {
      key: 'branch',
      header: 'Branch',
      accessor: (row) => row.Branch?.name || 'N/A',
    },
    {
      key: 'department',
      header: 'Department',
      accessor: (row) => row.Department?.name || 'N/A',
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    const actions = [];
    
    actions.push({
      label: 'View',
      icon: 'fas fa-eye',
      onClick: () => navigate(`/staff/staffDetails/${row.id}`),
    });

    actions.push({
      label: 'Edit',
      icon: 'fas fa-edit',
      onClick: () => navigate(`/staff/editStaff/${row.id}`),
    });

    // Get status value (handle both string and object)
    let statusValue = 'Active';
    if (typeof row.status === 'string') {
      statusValue = row.status;
    } else if (row.status && typeof row.status === 'object') {
      statusValue = row.status.value || row.status.name || row.status.status || 'Active';
    } else if (row.status) {
      statusValue = String(row.status);
    }

    if (statusValue === 'Active') {
      actions.push({
        label: 'Deactivate',
        icon: 'fas fa-ban',
        className: 'text-warning',
        onClick: () => {
          setSelected(row);
          setShowDeleteModal(true);
        },
      });
    } else {
      actions.push({
        label: 'Activate',
        icon: 'fas fa-check',
        className: 'text-success',
        onClick: () => handleActivate(row.id),
      });
    }

    return actions;
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handlePageChange(i)}
          disabled={loading}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {(currentPage - 1) * pagination.itemsPerPage + 1} to {Math.min(currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} entries
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          {pages}
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <Row style={{marginBottom: "0px !important"}}>
      <ToastContainer />
      <Col lg={12}>
        <Card className="user-list-card">
          <Card.Header style={{ display: 'block' }}>
            <div className="d-flex flex-row justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="search-input-group" style={{ flex: '1', minWidth: '150px' }}>
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by employee code, name..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                <Link to='/staff/addStaff' className="new-user-btn" style={{ whiteSpace: 'nowrap' }}>
                  <i className="fas fa-plus me-2"></i>
                  Add Staff
                </Link>
              </div>
            </div>
            
            {/* Filters */}
            {/* <div className="row g-2">
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={handleStatusChange}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Resigned">Resigned</option>
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={branchFilter}
                  onChange={handleBranchChange}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={departmentFilter}
                  onChange={handleDepartmentChange}
                  disabled={!branchFilter}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={subDepartmentFilter}
                  onChange={handleSubDepartmentChange}
                  disabled={!departmentFilter}
                >
                  <option value="">All Sub Departments</option>
                  {subDepartments.map((sd) => (
                    <option key={sd.id} value={sd.id}>
                      {sd.name}
                    </option>
                  ))}
                </select>
              </div>
              {(statusFilter || branchFilter || departmentFilter || subDepartmentFilter || search) && (
                <div className="col-md-2">
                  <button
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => {
                      setStatusFilter('');
                      setBranchFilter('');
                      setDepartmentFilter('');
                      setSubDepartmentFilter('');
                      setSearch('');
                      setCurrentPage(1);
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div> */}
          </Card.Header>
          <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1rem' }} className="p-md-3">
            {loading && staff.length === 0 ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <>
                <div className="row">
                  <CommonTable
                    columns={columns}
                    data={staff}
                    actions={getActions}
                    emptyMessage={staff.length === 0 ? 'No staff found' : 'No staff match your search criteria'}
                    emptyStateIcon="fas fa-users"
                    getRowId={(row, index) => row.id || index}
                    maxHeight="70vh"
                    minHeight="400px"
                    onRowClick={(row, rowIndex, e) => {
                      // Don't navigate if clicking on action button or action dropdown
                      if (e.target.closest('.table-actions') || e.target.closest('.dropdown-menu')) {
                        return;
                      }
                      navigate(`/staff/staffDetails/${row.id}`);
                    }}
                  />
                </div>
                {renderPagination()}
              </>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deactivate</Modal.Title>
        </Modal.Header>
        <Form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
          <Modal.Body>
            Are you sure you want to deactivate staff "{selected?.first_name && selected?.last_name ? `${selected.first_name} ${selected.last_name}` : selected?.employee_code || 'this staff'}"? This action will mark them as inactive.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button variant="warning" type="submit" disabled={deleteLoading}>
              {deleteLoading ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <BackToTop />
      <InternalFooter />
    </Row>
  );
};

export default StaffList;
